"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FuehrungsrinnenConfigCreator = void 0;
const echain_shared_logging_1 = require("@igus/echain-shared-logging");
const echain_shared_ts_utils_1 = require("@igus/echain-shared-ts-utils");
const echain_shared_articles_oo_1 = require("@igus/echain-shared-articles-oo");
const echain_shared_articles_1 = require("@igus/echain-shared-articles");
const FuehrungsrinneConfigError_1 = require("./FuehrungsrinneConfigError");
const ConfigurationUnion_1 = require("../ConfigurationUnion");
/**
 * The Service provides the ability to calculate Fuehrungsrinnen configurations for the
 * given input. If a configuration couldn't be calculated, then a warning will be logged.
 */
class FuehrungsrinnenConfigCreator {
    /**
     * Finds a Fuehrungsrinne without Gleitschiene by the given FuehrungsrinneTyp that is compatible with the given Kette.
     * The search is performed on the given Fuehrungsrinne list.
     * If no Fuehrungsrinne could be found then undefined is returned.
     *
     * @param fuehrungsrinneTyp The given Fuehrungsrinnetyp
     * @param kette The given Kette
     * @param fuehrungsrinneList List of all Fuehrungsrinnen
     */
    static findFuehrungsrinneByType(fuehrungsrinneTyp, kette, fuehrungsrinneList) {
        const fuehrungsrinne = echain_shared_articles_oo_1.FuehrungsrinnenFilter.filterFuehrungsrinnenByKette(fuehrungsrinneList, kette).find((rinne) => rinne.isFuehrungsrinne() && rinne.typ === fuehrungsrinneTyp);
        return fuehrungsrinne;
    }
    /**
     * Finds a Fuehrungsrinne without Gleitschiene by the given ArtNr that is compatible with the given Kette.
     * The search is performed on the given Fuehrungsrinne list.
     * If no Fuehrungsrinne could be found then undefined is returned.
     *
     * @param fuehrungsrinneArtNr The given Fuehrungsrinne ArtNr
     * @param kette The given Kette
     * @param fuehrungsrinneList List of all Fuehrungsrinnen
     */
    static findFuehrungsrinneByArtNr(fuehrungsrinneArtNr, kette, fuehrungsrinneList) {
        const fuehrungsrinne = echain_shared_articles_oo_1.FuehrungsrinnenFilter.filterFuehrungsrinnenByKette(fuehrungsrinneList, kette).find((rinne) => rinne.isFuehrungsrinne() && rinne.artNr === fuehrungsrinneArtNr);
        return fuehrungsrinne;
    }
    /**
     * Finds a Fuehrungsrinne with Gleitschiene that is compatible with the given Fuehrungsrinne and Kette.
     *
     * @param fuehrungsrinne The given Fuehrungsrinne
     * @param kette The given Kette
     * @param fuehrungsrinneList A list of all Fuehrungsrinnen
     */
    static findFuehrungsrinneGleitschiene(fuehrungsrinne, kette, fuehrungsrinneList, useSlh) {
        const fuehrungsrinneGleitschiene = echain_shared_articles_oo_1.FuehrungsrinnenFilter.filterFuehrungsrinnenByFuehrungsrinneKette(fuehrungsrinneList, fuehrungsrinne, kette).find((rinne) => {
            const result = rinne.isFuehrungsrinneGleitschiene();
            if (useSlh != null) {
                return result && rinne.isSLH === useSlh;
            }
            return result;
        });
        return fuehrungsrinneGleitschiene;
    }
    /**
     * Finds Montage Sets that are used to fixate a corresponding Fuehrungsrinne by the given Kette
     * and Fuehrungsrinne. The Montage Set result list is sorted in order to ensure that Montage Sets
     * with C-Profil appear before Montage Sets without C-Profil.
     *
     * @param fuehrungsrinne the given Fuehrungsrinne
     * @param kette the given Kette
     * @param montageSetList a list of all Montage Sets
     */
    static findAvailableMontageSets(fuehrungsrinne, kette, montageSetList) {
        const montageSets = echain_shared_articles_oo_1.FuehrungsrinnenFilter.filterMontageSetsByFuehrungsrinneKetteMontageSetTyp(montageSetList, fuehrungsrinne, kette).sort((a, b) => {
            if (a.isBefestigungInnen()) {
                if (b.isBefestigungInnen()) {
                    return 0;
                }
                else if (b.isBefestigungAussen()) {
                    return -1;
                }
                else if (b.isMontageSetMitCProfil()) {
                    return -1;
                }
                else {
                    return -1;
                }
            }
            else if (a.isBefestigungAussen()) {
                if (b.isBefestigungInnen()) {
                    return 1;
                }
                else if (b.isBefestigungAussen()) {
                    return 0;
                }
                else if (b.isMontageSetMitCProfil()) {
                    return -1;
                }
                else {
                    return -1;
                }
            }
            else if (a.isMontageSetMitCProfil()) {
                if (b.isBefestigungInnen()) {
                    return 1;
                }
                else if (b.isBefestigungAussen()) {
                    return 1;
                }
                else if (b.isMontageSetMitCProfil()) {
                    return 0;
                }
                else {
                    return -1;
                }
            }
            else {
                if (b.isBefestigungInnen()) {
                    return 1;
                }
                else if (b.isBefestigungAussen()) {
                    return 1;
                }
                else if (b.isMontageSetMitCProfil()) {
                    return 1;
                }
                else {
                    return 0;
                }
            }
        });
        return montageSets;
    }
    static findAvailableMontageSetsByConfig(fuehrungsrinne, configModel, montageSetList) {
        const { type, config } = (0, ConfigurationUnion_1.asTypedConfigurationUnion)(configModel);
        switch (type) {
            case 'Empty':
            case 'Espool':
            case 'EspoolChild':
                return [];
            case 'Kette':
                return this.findAvailableMontageSets(fuehrungsrinne, (0, echain_shared_articles_oo_1.A)(config.Kette), montageSetList);
            default:
                (0, echain_shared_ts_utils_1.assertUnreachable)(type);
        }
    }
    /**
     * Finds all Festpunktmodule that are compatible with the given Fuehrungsrinne and the given Kette.
     *
     * @param fuehrungsrinne The given Fuehrungsrinne
     * @param kette The given Kette
     * @param ae The AE of the Kette at the Festpunkt
     * @param montageSetList List of all Montage Sets
     */
    static findAvailableFestpunktmodule(fuehrungsrinne, kette, ae, montageSetList) {
        const festpunktmodule = echain_shared_articles_oo_1.FuehrungsrinnenFilter.filterMontageSetsByFuehrungsrinneKetteFestpunktmodulTyp(montageSetList, fuehrungsrinne, kette, ae);
        return festpunktmodule;
    }
    /**
     * Creates a Fuehrungsrinnen configuration proposal for the given Fuehrungsrinne.
     *
     * @param fuehrungsrinne The Fuehrungsrinne a configuration proposal should be created for
     * @param kette The given Ketten
     * @param fuehrungsrinneList A list of all Fuehrungsrinnen
     */
    static createFuehrungsrinneConfiguration(fuehrungsrinne, configModel, fuehrungsrinneList, useFestpunktmodul, useSlh) {
        let ketteArtNr = '';
        let kette;
        const { type, config } = (0, ConfigurationUnion_1.asTypedConfigurationUnion)(configModel);
        switch (type) {
            case 'Empty':
            case 'Espool':
            case 'EspoolChild':
                throw new Error(`${this.className}.createFuehrungsrinneConfiguration wrong type(${type}) should be Kette or RolEchain`);
            case 'Kette':
                kette = (0, echain_shared_articles_oo_1.A)(config.Kette);
                ketteArtNr = kette.getQuoteArtNr() || '';
                break;
            default:
                (0, echain_shared_ts_utils_1.assertUnreachable)(type);
        }
        // Fuehrungsrinne
        if (!fuehrungsrinne) {
            (0, echain_shared_logging_1.getLogger)().warn(`Couldn't find Fuehrungsrinne for Kette`, ketteArtNr);
            return new FuehrungsrinneConfigError_1.FuehrungsrinnenConfigError(FuehrungsrinneConfigError_1.FuehrungsrinnenConfigErrorType.Fuehrungsrinne);
        }
        const fuehrungsrinneStuecklaenge = fuehrungsrinne.calculateStuecklange();
        if (!fuehrungsrinneStuecklaenge) {
            (0, echain_shared_logging_1.getLogger)().warn(`Couldn't calculate Fuehrungsrinne piece length`, fuehrungsrinne.getQuoteArtNr());
            return new FuehrungsrinneConfigError_1.FuehrungsrinnenConfigError(FuehrungsrinneConfigError_1.FuehrungsrinnenConfigErrorType.FuehrungsrinneStuecklaenge);
        }
        fuehrungsrinne.laenge = fuehrungsrinneStuecklaenge;
        const useFestpunktmodulRinne = fuehrungsrinne.typ === echain_shared_articles_1.FuehrungsrinneTyp.Stahl &&
            !fuehrungsrinne.isFuehrungsrinneOhneMontageSet() &&
            useFestpunktmodul;
        // Fuehrungsrinne mit Gleitschiene
        const fuehrungsrinneGleitschiene = this.findFuehrungsrinneGleitschiene(fuehrungsrinne, kette, fuehrungsrinneList, useSlh);
        if (!fuehrungsrinneGleitschiene) {
            (0, echain_shared_logging_1.getLogger)().warn(`Couldn't find Fuehrungsrinne mit Gleitschiene for given Fuehrungsrinne and Kette`, {
                fuehrungsrinne: fuehrungsrinne.getQuoteArtNr(),
                kette: { ketteArtNr, type },
            });
            return new FuehrungsrinneConfigError_1.FuehrungsrinnenConfigError(FuehrungsrinneConfigError_1.FuehrungsrinnenConfigErrorType.FuehrungsrinneGleitschiene);
        }
        const fuehrungsrinneGleitschieneStuecklaenge = fuehrungsrinneGleitschiene.calculateStuecklange();
        if (!fuehrungsrinneGleitschieneStuecklaenge) {
            (0, echain_shared_logging_1.getLogger)().warn(`Couldn't calculate Fuehrungsrinne mit Gleitschiene piece length`, fuehrungsrinneGleitschiene.getQuoteArtNr());
            return new FuehrungsrinneConfigError_1.FuehrungsrinnenConfigError(FuehrungsrinneConfigError_1.FuehrungsrinnenConfigErrorType.FuehrungsrinneGleitschieneStuecklaenge);
        }
        fuehrungsrinneGleitschiene.laenge = fuehrungsrinneGleitschieneStuecklaenge;
        // Festpunktmodul Stahl-Fuehrungsrinne
        let festpunktmodulFuehrungsrinne;
        if (useFestpunktmodulRinne) {
            const compatibleFuehrungsrinnen = echain_shared_articles_oo_1.FuehrungsrinnenFilter.filterFuehrungsrinnenByFuehrungsrinneKette(fuehrungsrinneList, fuehrungsrinne, kette);
            festpunktmodulFuehrungsrinne = compatibleFuehrungsrinnen.find((rinne) => rinne.isFestpunktmodulFuehrungsrinne());
            if (!festpunktmodulFuehrungsrinne) {
                (0, echain_shared_logging_1.getLogger)().warn(`Couldn't find a Festpunktmodul Fuehrungsrinne for given Fuehrungsrinne and Kette`, {
                    fuehrungsrinne: fuehrungsrinne.getQuoteArtNr(),
                    kette: { ketteArtNr, type },
                });
                return new FuehrungsrinneConfigError_1.FuehrungsrinnenConfigError(FuehrungsrinneConfigError_1.FuehrungsrinnenConfigErrorType.FuehrungsrinneFestpunktmodul);
            }
            const festpunktmodulFuehrungsrinneStuecklaenge = festpunktmodulFuehrungsrinne.calculateStuecklange();
            if (!festpunktmodulFuehrungsrinneStuecklaenge) {
                (0, echain_shared_logging_1.getLogger)().warn(`Couldn't calculate Festpunktmodul Fuehrungsrinne piece length`, festpunktmodulFuehrungsrinne.getQuoteArtNr());
                return new FuehrungsrinneConfigError_1.FuehrungsrinnenConfigError(FuehrungsrinneConfigError_1.FuehrungsrinnenConfigErrorType.FuehrungsrinneFestpunktmodulStuecklaenge);
            }
            festpunktmodulFuehrungsrinne.laenge = festpunktmodulFuehrungsrinneStuecklaenge;
        }
        // Calculate Laenge
        const laengeGesamt = fuehrungsrinne.calculateLaengeGesamt(kette.toModel(), useFestpunktmodulRinne);
        if (!laengeGesamt) {
            (0, echain_shared_logging_1.getLogger)().warn(`Couldn't calculate the length of the Fuehrungsrinne`, {
                fuehrungsrinne: fuehrungsrinne.getQuoteArtNr(),
                kette: { ketteArtNr, type },
            });
            return new FuehrungsrinneConfigError_1.FuehrungsrinnenConfigError(FuehrungsrinneConfigError_1.FuehrungsrinnenConfigErrorType.FuehrungsrinneGesamtLaenge);
        }
        const fuehrungsrinneLaenge = fuehrungsrinne.calculateLaengeOhneGleitschiene(kette.toModel(), useFestpunktmodulRinne);
        if (!fuehrungsrinneLaenge) {
            (0, echain_shared_logging_1.getLogger)().warn(`Couldn't calculate the length of the Fuehrungsrinne ohne Gleitschiene`, {
                fuehrungsrinne: fuehrungsrinne.getQuoteArtNr(),
                kette: { ketteArtNr, type },
            });
            return new FuehrungsrinneConfigError_1.FuehrungsrinnenConfigError(FuehrungsrinneConfigError_1.FuehrungsrinnenConfigErrorType.FuehrungsrinneLaenge);
        }
        const fuehrungsrinneGleitschieneLaenge = fuehrungsrinne.calculateLaengeMitGleitschiene(laengeGesamt, fuehrungsrinneLaenge, useFestpunktmodulRinne);
        if (!fuehrungsrinneGleitschieneLaenge) {
            (0, echain_shared_logging_1.getLogger)().warn(`Couldn't calculate the length of the Fuehrungsrinne mit Gleitschiene`, {
                fuehrungsrinneGleitschiene: fuehrungsrinneGleitschiene.getQuoteArtNr(),
                kette: { ketteArtNr, type },
                laengeGesamt,
                fuehrungsrinneLaenge,
            });
            return new FuehrungsrinneConfigError_1.FuehrungsrinnenConfigError(FuehrungsrinneConfigError_1.FuehrungsrinnenConfigErrorType.FuehrungsrinneGleitschieneLaenge);
        }
        // Calculate Count
        const fuehrungsrinneCount = fuehrungsrinne.calculateAnzahlOhneGleitschiene(fuehrungsrinneLaenge, useFestpunktmodulRinne);
        if (!fuehrungsrinneCount && fuehrungsrinneCount !== 0) {
            (0, echain_shared_logging_1.getLogger)().warn(`Couldn't calculate the Fuehrungsrinne ohne Gleitschiene count`, {
                fuehrungsrinne: fuehrungsrinne.getQuoteArtNr(),
                kette: { ketteArtNr, type },
                fuehrungsrinneLaenge,
            });
            return new FuehrungsrinneConfigError_1.FuehrungsrinnenConfigError(FuehrungsrinneConfigError_1.FuehrungsrinnenConfigErrorType.FuehrungsrinneAnzahl);
        }
        const fuehrungsrinneGleitschieneCount = fuehrungsrinneGleitschiene.calculateAnzahlMitGleitschiene(fuehrungsrinneGleitschieneLaenge, useFestpunktmodulRinne);
        if (!fuehrungsrinneGleitschieneCount && fuehrungsrinneGleitschieneCount !== 0) {
            (0, echain_shared_logging_1.getLogger)().warn(`Couldn't calculate the Fuehrungsrinne mit Gleitschiene count`, {
                fuehrungsrinneGleitschiene: fuehrungsrinneGleitschiene.getQuoteArtNr(),
                kette: { ketteArtNr, type },
                fuehrungsrinneGleitschieneLaenge,
            });
            return new FuehrungsrinneConfigError_1.FuehrungsrinnenConfigError(FuehrungsrinneConfigError_1.FuehrungsrinnenConfigErrorType.FuehrungsrinneGleitschieneAnzahl);
        }
        // Create result
        const result = {
            fuehrungsrinnen: { article: fuehrungsrinne, count: fuehrungsrinneCount },
            fuehrungsrinnenMitGleitschiene: { article: fuehrungsrinneGleitschiene, count: fuehrungsrinneGleitschieneCount },
        };
        if (festpunktmodulFuehrungsrinne) {
            result.fuehrungsrinneFestpunktmodul = { article: festpunktmodulFuehrungsrinne, count: 1 };
        }
        return result;
    }
    /**
     * Creates a Montage Set configuration proposal by the given Fuehrungsrinne configuration proposal and the given Montage Set.
     *
     * @param fuehrungsrinnenConfiguration The given Fuehrungsrinne configuration proposal
     * @param montageSet The given Montage Set
     * @param kette The given Kette
     * @param montageSetList A list of all Montage Sets
     * @param ae The AE at the Festpunkt of the Kette
     */
    static createMontageSetConfiguration(fuehrungsrinnenConfiguration, montageSet, configModel, montageSetList, useFestpunktmodul, ae) {
        let ketteArtNr = '';
        let kette;
        const { type, config } = (0, ConfigurationUnion_1.asTypedConfigurationUnion)(configModel);
        switch (type) {
            case 'Empty':
            case 'Espool':
            case 'EspoolChild':
                throw new Error(`${this.className}.createFuehrungsrinneConfiguration wrong type(${type}) should be Kette or RolEchain`);
            case 'Kette':
                kette = (0, echain_shared_articles_oo_1.A)(config.Kette);
                ketteArtNr = kette.getQuoteArtNr() || '';
                break;
            default:
                (0, echain_shared_ts_utils_1.assertUnreachable)(type);
        }
        // Montage Set
        if (!montageSet) {
            (0, echain_shared_logging_1.getLogger)().warn(`Couldn't find Montage Set for Kette and Fuehrungsrinne`, {
                kette: { ketteArtNr, type },
                fuehrungsrinnenConfiguration,
            });
            return new FuehrungsrinneConfigError_1.FuehrungsrinnenConfigError(FuehrungsrinneConfigError_1.FuehrungsrinnenConfigErrorType.MontageSet);
        }
        // Calculate Montage Set count
        const montageSetAnzahl = montageSet.calculateAnzahl(fuehrungsrinnenConfiguration.fuehrungsrinnen.article, fuehrungsrinnenConfiguration.fuehrungsrinnen.count, fuehrungsrinnenConfiguration.fuehrungsrinnenMitGleitschiene.count, useFestpunktmodul);
        if (!montageSetAnzahl) {
            (0, echain_shared_logging_1.getLogger)().warn(`Couldn't calculate the Montage Set count`, {
                montageSet: montageSet.getQuoteArtNr(),
                fuehrungsrinne: fuehrungsrinnenConfiguration.fuehrungsrinnen.article.getQuoteArtNr(),
                fuehrungsrinnenCount: fuehrungsrinnenConfiguration.fuehrungsrinnen.count,
                fuehrungsrinnenMitGleitschieneCount: fuehrungsrinnenConfiguration.fuehrungsrinnenMitGleitschiene.count,
            });
            return new FuehrungsrinneConfigError_1.FuehrungsrinnenConfigError(FuehrungsrinneConfigError_1.FuehrungsrinnenConfigErrorType.MontageSetAnzahl);
        }
        // Find Festpunktmodul
        let festpunktModul;
        if (useFestpunktmodul && ae) {
            const festpunktmodule = this.findAvailableFestpunktmodule(fuehrungsrinnenConfiguration.fuehrungsrinnen.article, kette, ae, montageSetList);
            festpunktModul = festpunktmodule && festpunktmodule.length > 0 ? festpunktmodule[0] : undefined;
        }
        // Create result
        const result = {
            montageSets: { article: montageSet, count: montageSetAnzahl },
        };
        if (festpunktModul && festpunktModul instanceof echain_shared_articles_oo_1.MontageSet) {
            result.montageSetFestpunktmodul = { article: festpunktModul, count: 1 };
        }
        return result;
    }
    /**
     * Creates a complete configuration proposal that includes Fuehrungsrinnen and Montage Sets for the given Fuehrungsrinne and Kette.
     *
     * @param fuehrungsrinne The given Fuehrungsrinne
     * @param kette The given Kette
     * @param fuehrungsrinneList A list of all Fuehrungsrinnen
     * @param montageSetList A list of all Montage Sets
     * @param ae The AE at the Festpunkt of the Kette
     */
    static createConfigurationProposals(fuehrungsrinne, configModel, fuehrungsrinneList, montageSetList, useFestpunktmodul, ae, useSLH) {
        const result = [];
        let ketteArtNr = '';
        const { type, config } = (0, ConfigurationUnion_1.asTypedConfigurationUnion)(configModel);
        switch (type) {
            case 'Empty':
            case 'Espool':
            case 'EspoolChild':
                throw new Error(`${this.className}.createFuehrungsrinneConfiguration wrong type(${type}) should be Kette or RolEchain`);
            case 'Kette':
                ketteArtNr = (0, echain_shared_articles_oo_1.A)(config.Kette).getQuoteArtNr() || '';
                break;
            default:
                (0, echain_shared_ts_utils_1.assertUnreachable)(type);
        }
        const fuehrungsrinneResult = this.createFuehrungsrinneConfiguration(fuehrungsrinne, configModel, fuehrungsrinneList, useFestpunktmodul, useSLH);
        if (!(fuehrungsrinneResult instanceof FuehrungsrinneConfigError_1.FuehrungsrinnenConfigError)) {
            const availableMontageSets = this.findAvailableMontageSetsByConfig(fuehrungsrinneResult.fuehrungsrinnen.article, configModel, montageSetList);
            // TODO What about Rinnen without Montage Sets?
            if (availableMontageSets && availableMontageSets.length > 0) {
                availableMontageSets.forEach((montageSet) => {
                    const montageSetResult = this.createMontageSetConfiguration(fuehrungsrinneResult, montageSet, configModel, montageSetList, useFestpunktmodul, ae);
                    if (!(montageSetResult instanceof FuehrungsrinneConfigError_1.FuehrungsrinnenConfigError)) {
                        result.push({
                            fuehrungsrinnen: {
                                article: fuehrungsrinneResult.fuehrungsrinnen.article,
                                count: fuehrungsrinneResult.fuehrungsrinnen.count,
                            },
                            fuehrungsrinnenMitGleitschiene: {
                                article: fuehrungsrinneResult.fuehrungsrinnenMitGleitschiene.article,
                                count: fuehrungsrinneResult.fuehrungsrinnenMitGleitschiene.count,
                            },
                            fuehrungsrinneFestpunktmodul: fuehrungsrinneResult.fuehrungsrinneFestpunktmodul
                                ? {
                                    article: fuehrungsrinneResult.fuehrungsrinneFestpunktmodul.article,
                                    count: fuehrungsrinneResult.fuehrungsrinneFestpunktmodul.count,
                                }
                                : undefined,
                            montageSets: montageSetResult.montageSets,
                            montageSetFestpunktmodul: montageSetResult.montageSetFestpunktmodul,
                        });
                    }
                });
            }
            else {
                (0, echain_shared_logging_1.getLogger)().warn(`Couldn't find any Montage Sets for the given Fuehrungsrinne and Kette`, {
                    fuehrungsrinne: fuehrungsrinneResult.fuehrungsrinnen.article.getQuoteArtNr(),
                    kette: { ketteArtNr, type },
                });
            }
        }
        return result;
    }
    /**
     * An array of complete configuration proposals is filtered by the given Bauraum.
     */
    static filterConfigurationProposalsByBauraum(configurationProposals, configModel, width, height, length) {
        let kette;
        const { type, config } = (0, ConfigurationUnion_1.asTypedConfigurationUnion)(configModel);
        switch (type) {
            case 'Empty':
            case 'Espool':
            case 'EspoolChild':
                throw new Error(`${this.className}.createFuehrungsrinneConfiguration wrong type(${type}) should be Kette or RolEchain`);
            case 'Kette':
                kette = (0, echain_shared_articles_oo_1.A)(config.Kette);
                break;
            default:
                (0, echain_shared_ts_utils_1.assertUnreachable)(type);
        }
        return configurationProposals.filter((proposal) => {
            if (kette.minBauraumGleitendRinneRefs && kette.minBauraumGleitendRinneRefs.length > 0) {
                const minRef = kette.minBauraumGleitendRinneRefs.find((bauraumRef) => {
                    return (bauraumRef.fuehrungsrinneId === proposal.fuehrungsrinnen.article.id &&
                        bauraumRef.montageSetId === proposal.montageSets.article.id);
                });
                if (minRef) {
                    if (width && minRef.breite && width.mm < minRef.breite.mm) {
                        return false;
                    }
                    if (height && minRef.hoehe && height.mm < minRef.hoehe.mm) {
                        return false;
                    }
                    if (length) {
                        const useFestpunktmodulRinne = Boolean(proposal.fuehrungsrinneFestpunktmodul && proposal.fuehrungsrinneFestpunktmodul.count > 0);
                        const laengeGesamt = proposal.fuehrungsrinnen.article.calculateLaengeGesamt(kette.toModel(), useFestpunktmodulRinne);
                        if (laengeGesamt) {
                            const minLaengeBauraum = proposal.fuehrungsrinnen.article.calculateMinBauraumLaenge(laengeGesamt);
                            if (minLaengeBauraum && length.mm < minLaengeBauraum.mm) {
                                return false;
                            }
                        }
                    }
                }
            }
            return true;
        });
    }
    /**
     * Finds all Ablegerinnen for the given Kette.
     *
     * @param kette The given Kette
     * @param fuehrungsrinneList A list of all Fuehrungsrinnen
     */
    static findAblegerinnen(kette, fuehrungsrinneList) {
        const fuehrungsrinnen = echain_shared_articles_oo_1.FuehrungsrinnenFilter.filterFuehrungsrinnenByKette(fuehrungsrinneList, kette).filter((rinne) => rinne.opti === echain_shared_articles_1.FuehrungsrinneOpti.ABLEGERINNE_EINZELND_1M ||
            rinne.opti === echain_shared_articles_1.FuehrungsrinneOpti.ABLEGERINNE_EINZELND_2M ||
            rinne.opti === echain_shared_articles_1.FuehrungsrinneOpti.ABLEGERINNE_WANDKONSOLE_1M ||
            rinne.opti === echain_shared_articles_1.FuehrungsrinneOpti.ABLEGERINNE_WANDKONSOLE_2M);
        return fuehrungsrinnen;
    }
    /**
     * Determines if a complete configuration proposal is equal to another complete configuration proposal.
     *
     * @param a A complete configuration proposal
     * @param b Another complete configuration proposal
     */
    static areProposalsEqual(a, b) {
        if (!a && !b) {
            return true;
        }
        else if (a && b) {
            let result = true;
            result = result && Boolean(a.fuehrungsrinnen) === Boolean(b.fuehrungsrinnen);
            if (a.fuehrungsrinnen && b.fuehrungsrinnen) {
                result = result && a.fuehrungsrinnen.count === b.fuehrungsrinnen.count;
                result = result && a.fuehrungsrinnen.article.id === b.fuehrungsrinnen.article.id;
            }
            result = result && Boolean(a.fuehrungsrinnenMitGleitschiene) === Boolean(b.fuehrungsrinnenMitGleitschiene);
            if (a.fuehrungsrinnenMitGleitschiene && b.fuehrungsrinnenMitGleitschiene) {
                result = result && a.fuehrungsrinnenMitGleitschiene.count === b.fuehrungsrinnenMitGleitschiene.count;
                result = result && a.fuehrungsrinnenMitGleitschiene.article.id === b.fuehrungsrinnenMitGleitschiene.article.id;
            }
            result = result && Boolean(a.fuehrungsrinneFestpunktmodul) === Boolean(b.fuehrungsrinneFestpunktmodul);
            if (a.fuehrungsrinneFestpunktmodul && b.fuehrungsrinneFestpunktmodul) {
                result = result && a.fuehrungsrinneFestpunktmodul.count === b.fuehrungsrinneFestpunktmodul.count;
                result = result && a.fuehrungsrinneFestpunktmodul.article.id === b.fuehrungsrinneFestpunktmodul.article.id;
            }
            result = result && Boolean(a.montageSets) === Boolean(b.montageSets);
            if (a.montageSets && b.montageSets) {
                result = result && a.montageSets.count === b.montageSets.count;
                result = result && a.montageSets.article.id === b.montageSets.article.id;
            }
            result = result && Boolean(a.montageSetFestpunktmodul) === Boolean(b.montageSetFestpunktmodul);
            if (a.montageSetFestpunktmodul && b.montageSetFestpunktmodul) {
                result = result && a.montageSetFestpunktmodul.count === b.montageSetFestpunktmodul.count;
                result = result && a.montageSetFestpunktmodul.article.id === b.montageSetFestpunktmodul.article.id;
            }
            return result;
        }
        return false;
    }
}
exports.FuehrungsrinnenConfigCreator = FuehrungsrinnenConfigCreator;
FuehrungsrinnenConfigCreator.className = 'FuehrungsrinnenConfigCreator';
//# sourceMappingURL=FuehrungsrinnenConfigCreator.js.map